import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Award, Loader2 } from "lucide-react";
import { fetchQuotaMe, type QuotaMeResponse } from "../../modules/tutor/api/quota.client";
import {
  formatSessionCountdown,
  sessionRemainingSecondsFromExpiresAt,
} from "../../modules/tutor/api/quotaRuntime.client";
import { fetchTutorSystemCapabilities } from "../../modules/tutor/api/tutorCapabilities.client";
import { labelForTutorFeature } from "../../modules/tutor/api/quotaLabels";
import { useTutorRuntimeQuota } from "../../modules/tutor/hooks/useTutorRuntimeQuota";
import {
  quotaFeatureNameForScope,
  resolveFeatureTodayMinutes,
  resolveMonthlyUsageProgress,
  resolvePooledTodayMinutes,
  resolveSidebarProgressScope,
  type SidebarProgressScope,
} from "./sidebarMyProgressUtils";

const POOL_SCOPE: SidebarProgressScope = { mode: "pool", caption: "All live tutors" };

export function SidebarMyProgress() {
  const location = useLocation();
  const scopeIdentity = useMemo(
    () => quotaFeatureNameForScope(resolveSidebarProgressScope(location.pathname)),
    [location.pathname],
  );
  const scope = useMemo((): SidebarProgressScope => {
    if (scopeIdentity === "live_ai_tutor") {
      return POOL_SCOPE;
    }
    return {
      mode: "feature",
      featureName: scopeIdentity,
      caption: labelForTutorFeature(scopeIdentity),
    };
  }, [scopeIdentity]);
  const featureName = scopeIdentity;
  const [quota, setQuota] = useState<QuotaMeResponse | null>(null);
  const [runtimeV2, setRuntimeV2] = useState(false);
  const [v1Loading, setV1Loading] = useState(true);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const {
    runtimeQuota,
    isLoading: runtimeQuotaLoading,
    isFetched: runtimeQuotaFetched,
  } = useTutorRuntimeQuota({
    featureName,
    enabled: true,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const caps = await fetchTutorSystemCapabilities().catch(() => null);
      const v2 = !!caps?.quota_runtime_v2_enabled;
      if (cancelled) return;
      setRuntimeV2(v2);
      if (v2) {
        setV1Loading(false);
        setQuota(null);
        return;
      }
      setV1Loading(true);
      try {
        const r = await fetchQuotaMe(featureName);
        if (!cancelled) setQuota(r);
      } catch {
        if (!cancelled) setQuota(null);
      } finally {
        if (!cancelled) setV1Loading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [featureName]);

  const loading = runtimeV2
    ? runtimeQuotaLoading && !runtimeQuotaFetched
    : v1Loading;

  useEffect(() => {
    if (!runtimeV2 || !runtimeQuota?.active_session?.expires_at) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [runtimeV2, runtimeQuota?.active_session?.expires_at]);

  const { used, limit, percent } = useMemo(() => {
    if (runtimeV2 && runtimeQuota) {
      return resolveMonthlyUsageProgress(runtimeQuota);
    }
    if (scope.mode === "pool") {
      return resolvePooledTodayMinutes(quota);
    }
    return resolveFeatureTodayMinutes(quota);
  }, [quota, scope.mode, runtimeV2, runtimeQuota]);

  const sessionCountdown = useMemo(() => {
    if (!runtimeV2 || !runtimeQuota?.active_session?.expires_at) return null;
    return sessionRemainingSecondsFromExpiresAt(runtimeQuota.active_session.expires_at, nowTick);
  }, [runtimeV2, runtimeQuota, nowTick]);

  const goalLabel =
    limit != null ? `${used} / ${limit} min` : used > 0 ? `${used} min` : "—";

  const cheer =
    percent >= 100
      ? "Monthly budget used — see you next month!"
      : percent >= 50
        ? "Great job! Keep shining!"
        : "Let's learn something new today!";

  return (
    <div className="relative overflow-hidden rounded-2xl border-2 border-app-primary/30/70 dark:border-violet-500/40 bg-gradient-to-br from-amber-50 via-violet-100 to-violet-200 dark:from-violet-950 dark:via-purple-950 dark:to-violet-900 shadow-md p-3 flex items-center gap-3">
      <div className="relative h-14 w-14 shrink-0">
        {loading ? (
          <div className="h-full w-full flex items-center justify-center rounded-full bg-white/70 dark:bg-app-primary/20/50">
            <Loader2 className="h-5 w-5 animate-spin text-app-primary dark:text-violet-300" aria-hidden />
          </div>
        ) : (
          <div className="relative h-14 w-14 rounded-full bg-white/80 dark:bg-app-primary/20/60 shadow-inner ring-2 ring-app-primary/20/80 dark:ring-violet-500/40">
            <svg className="h-14 w-14 -rotate-90" viewBox="0 0 56 56" aria-hidden>
              <circle cx="28" cy="28" r="24" fill="none" stroke="currentColor" strokeWidth="5" className="text-violet-200/90 dark:text-app-primary" />
              <circle
                cx="28"
                cy="28"
                r="24"
                fill="none"
                stroke="url(#sidebarProgGrad)"
                strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${(percent / 100) * 150.8} 150.8`}
              />
              <defs>
                <linearGradient id="sidebarProgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#7c3aed" />
                  <stop offset="100%" stopColor="#f59e0b" />
                </linearGradient>
              </defs>
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold text-app-primary dark:text-violet-100">
              {percent}%
            </span>
          </div>
        )}
      </div>

      <div className="relative min-w-0 flex-1">
        <p className="text-sm font-extrabold text-app-primary dark:text-violet-50">
          {runtimeV2 ? "Tutor usage" : "Tutor time"}
        </p>
        <p className="text-[9px] font-semibold uppercase tracking-wide text-app-primary/90 dark:text-violet-200/80 mt-0.5 truncate">
          {scope.caption}
        </p>
        {runtimeV2 && sessionCountdown != null ? (
          <p className="text-[10px] text-app-primary/90 dark:text-violet-200/90 mt-0.5">
            Current Session:{" "}
            <span className="font-bold text-app-primary dark:text-white">
              {formatSessionCountdown(sessionCountdown)}
            </span>
          </p>
        ) : null}
        <p className="text-[10px] text-app-primary/90 dark:text-violet-200/90 mt-0.5">
          {runtimeV2 ? "Monthly usage" : "Today's quota"}:{" "}
          <span className="font-bold text-app-primary dark:text-white">{goalLabel}</span>
        </p>
        <div className="mt-1.5 h-1.5 rounded-full bg-white/60 dark:bg-app-primary/15/60 overflow-hidden ring-1 ring-app-primary/20/50 dark:ring-violet-700/50">
          <div
            className="h-full rounded-full bg-gradient-to-r from-app-primary via-app-primary/90 to-amber-400 transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="text-[9px] text-app-primary dark:text-violet-200/90 mt-1 font-semibold">{cheer}</p>
      </div>

      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/75 dark:bg-amber-500/20 shadow-sm ring-1 ring-amber-300/60 dark:ring-amber-500/40">
        <Award className="h-5 w-5 text-amber-600 dark:text-amber-300" aria-hidden />
      </div>
    </div>
  );
}
